// A minimal markdown renderer for the one place this client shows prose it did not
// author: a demo's character sheet (`docs/demo-sheets/*.md`). Those files
// use exactly four constructs — `#`/`##` headings, blank-line-separated paragraphs,
// `- ` bullet lists, and `**bold**` inline — and nothing else, so this renders that
// subset by hand rather than adding a markdown dependency for four rules. See
// `docs/design-system.md`'s instruction to check for an existing renderer before adding
// one, and to render the small subset in use rather than a dependency when there is none.
//
// WHY THE LEADING "NN." IN EVERY SHEET IS NOT AN ORDERED LIST. Each sheet's first
// paragraph opens with a person's age written as prose ("45. You have been married..."),
// not a list marker — there is no ordered list anywhere in these files. A renderer that
// special-cased a leading digit-and-period as a list item would misrender every sheet's
// first line, so this one does not implement ordered lists at all.

type Block =
  | { kind: 'h1'; text: string }
  | { kind: 'h2'; text: string }
  | { kind: 'p'; text: string }
  | { kind: 'ul'; items: string[] };

function parseBlocks(source: string): Block[] {
  const lines = source.split('\n');
  const blocks: Block[] = [];
  // A paragraph is lines joined by a space, the way markdown soft-wraps within one —
  // these files wrap by hand at a fixed column, and a renderer that kept the line breaks
  // would show a paragraph as several short, ragged ones.
  let paragraph: string[] = [];
  let list: string[] = [];

  const flushParagraph = (): void => {
    if (paragraph.length > 0) {
      blocks.push({ kind: 'p', text: paragraph.join(' ') });
      paragraph = [];
    }
  };
  const flushList = (): void => {
    if (list.length > 0) {
      blocks.push({ kind: 'ul', items: list });
      list = [];
    }
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    if (line.trim() === '') {
      flushParagraph();
      flushList();
      continue;
    }
    if (line.startsWith('# ')) {
      flushParagraph();
      flushList();
      blocks.push({ kind: 'h1', text: line.slice(2).trim() });
      continue;
    }
    if (line.startsWith('## ')) {
      flushParagraph();
      flushList();
      blocks.push({ kind: 'h2', text: line.slice(3).trim() });
      continue;
    }
    if (line.startsWith('- ')) {
      flushParagraph();
      list.push(line.slice(2).trim());
      continue;
    }
    flushList();
    paragraph.push(line.trim());
  }
  flushParagraph();
  flushList();
  return blocks;
}

/** `**bold**`, the one inline rule these files use, split into plain and `<strong>` runs. */
function renderInline(text: string): JSX.Element[] {
  const parts = text.split(/(\*\*[^*]+\*\*)/gu);
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    }
    return <span key={index}>{part}</span>;
  });
}

export type MarkdownProps = {
  text: string;
  className?: string;
};

/** Renders the small subset of markdown `docs/demo-sheets/*.md` uses — see
 *  this file's header for exactly which four constructs that is. */
export function Markdown({ text, className }: MarkdownProps): JSX.Element {
  const blocks = parseBlocks(text);
  return (
    <div className={className}>
      {blocks.map((block, index) => {
        if (block.kind === 'h1') {
          return <h1 key={index}>{renderInline(block.text)}</h1>;
        }
        if (block.kind === 'h2') {
          return <h2 key={index}>{renderInline(block.text)}</h2>;
        }
        if (block.kind === 'ul') {
          return (
            <ul key={index}>
              {block.items.map((item, itemIndex) => (
                <li key={itemIndex}>{renderInline(item)}</li>
              ))}
            </ul>
          );
        }
        return <p key={index}>{renderInline(block.text)}</p>;
      })}
    </div>
  );
}
